import React, {
  Suspense, useState, useContext, useEffect, useRef
} from 'react';
import PropTypes from 'prop-types';
import { useBreakpoint } from '@one-thd/sui-atomic-components';
import { useConfigService, ExperienceContext } from '@thd-nucleus/experience-context';
import { useLocalStorage } from '@thd-olt-global/thd-storage-utils';
import { ImpressionProvider } from '@thd-olt-component-react/impression';
import { AssistantActionButton } from './feature/components/AssistantActionButton';
import {
  createActionId,
  getDefaultSession,
  isSessionExpired,
} from './feature/utils/assistant-utils';
import { useStickyElementOffset } from './hooks/useStickyElementOffset';
import { MAGIC_APRON_ASSISTANT_ORIGIN, MAGIC_APRON_CONTAINER_ID } from './constants/assistant-config';
import { getIsNewCompatiblePage, getIsLegacyCompatiblePage } from './utils/assistant-utils';
import './app.scss';

// THE single switch point.
// Reads the feature switch and lazy-loads exactly one of the two loader trees:
//   - master  -> ./App               (imports the master magic-apron package)
//   - feature -> ./feature/UnifiedApp (imports the aliased magic-apron-feature package)
// Because each App tree imports its own magic-apron package, this one decision
// transitively selects the underlying component build for the assistant content.
//
// The FAB and the drawer state that drives it are package-agnostic loader chrome,
// so they live here rather than inside either tree. Keeping them above the switch
// means the button renders regardless of which experience is (or is not yet)
// loaded, and the drawer state survives a mid-load flip of the feature switch —
// swapping the assistant content no longer resets which drawer the user has open.
// Retry a chunk import a few times before giving up, matching the resilience of
// the downstream dynamic imports (DynamicProv/DynamicUnified/DynamicAssistant). A
// single flaky fetch of an App chunk would otherwise fail the whole experience
// with no recovery.
const retryImport = (load, retries = 3) => load()
  .catch((err) => (retries > 0 ? retryImport(load, retries - 1) : Promise.reject(err)));

const MasterApp = React.lazy(
  () => retryImport(() => import('./App')).then((mod) => ({ default: mod.App }))
);
const FeatureApp = React.lazy(
  () => retryImport(() => import('./feature/UnifiedApp')).then((mod) => ({ default: mod.UnifiedApp }))
);

const defaultSession = getDefaultSession();

export const AppSwitch = ({ activateStyles }) => {
  const isLiveChatUnificationEnabled = useConfigService('isLiveChatUnificationEnabled');
  const isExclusionRulesEnabled = useConfigService('magic-apron-assistant-enable-exclusion-rules');
  const excludedPagesConfig = useConfigService('magic-apron-assistant-excluded-pages');
  const isCompatiblePage = (isLiveChatUnificationEnabled && isExclusionRulesEnabled)
    ? getIsNewCompatiblePage(excludedPagesConfig)
    : getIsLegacyCompatiblePage();

  useEffect(() => {
    if (isCompatiblePage && activateStyles) {
      activateStyles();
    }
  }, [isCompatiblePage]);

  // Commit the selected App tree to state from an effect rather than deriving it
  // inline during render. The config value can flip as it settles during page load;
  // swapping the rendered lazy component mid-flight would abort the in-progress
  // dynamic import (and its downstream chunk downloads). Holding the choice in state
  // lets a committed selection finish loading before we react to a later change.
  const [SelectedApp, setSelectedApp] = useState(null);

  useEffect(() => {
    setSelectedApp(() => (isLiveChatUnificationEnabled ? FeatureApp : MasterApp));
  }, [isLiveChatUnificationEnabled]);

  const { isConsumerApp, customer } = useContext(ExperienceContext);
  const [session, setSession] = useLocalStorage('maa-session', defaultSession);
  const sessionRef = useRef(session);
  useEffect(() => {
    sessionRef.current = session;
  }, [session]);
  const { lesser: isMobile } = useBreakpoint('lg');
  // Do not use this setter directly, use the updateDrawerState instead
  const [drawerState, setDrawerState] = useState('CLOSED');
  // Lazy-init so the channel is created once, not re-allocated (and leaked) on every render.
  const broadcastChannel = useRef(null);
  if (!broadcastChannel.current) {
    broadcastChannel.current = new BroadcastChannel('magic-apron-assistant');
  }
  const offsetMargin = useStickyElementOffset();
  // Mini drawer is disabled for B2B customers and while customer is escalated to agent
  // as per design decision
  const enableMiniDrawer = customer?.type !== 'b2b' && !session?.isEscalated;

  const fsDTEnabled = useConfigService('magic-apron-assistant-dt-enabled');
  const fsMWEnabled = useConfigService('magic-apron-assistant-mw-enabled');
  const fsAPPEnabled = useConfigService('magic-apron-assistant-app-enabled');
  const fsRenderOnB2B = useConfigService('magic-apron-assistant-b2b-enabled') ?? false;

  const isValidCustomerType = customer?.type === 'b2c' || (customer?.type === 'b2b' && fsRenderOnB2B);

  const updateSession = (sessionUpdates) => {
    sessionRef.current = { ...sessionRef.current, ...sessionUpdates };
    setSession(sessionRef.current);
  };

  const updateDrawerState = (newDrawerState, { skipBroadcast } = {}) => {
    setDrawerState(newDrawerState);
    if (!skipBroadcast) {
      // App opens new tabs instead of reloading current page. This updates drawer state across all app tabs.
      broadcastChannel.current.postMessage({
        type: 'INTERNAL_ONLY_MAA_DRAWER_STATE_CHANGED',
        data: { drawerState: newDrawerState }
      });
    }
  };

  const handleOpen = (isUserInteraction = false) => {
    updateDrawerState('OPEN');
    // Capture the prior flag before updateSession mutates sessionRef.current, so the
    // fab-click event still fires on the first interaction of the session.
    const alreadyInteracted = sessionRef.current?.hasSessionInteractedWithFab;
    updateSession({
      hasSessionInteractedWithFab: alreadyInteracted || isUserInteraction
    });
    if (isUserInteraction && !alreadyInteracted) {
      window.LIFE_CYCLE_EVENT_BUS.trigger('magic-apron-assistant.fab-click', {
        actionID: createActionId(sessionRef.current?.sessionId),
        sessionID: sessionRef.current?.sessionId,
      });
    }
  };

  const handleClose = () => {
    updateDrawerState('CLOSED');
    updateSession({ shouldAutoOpen: false });
    window.LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('magic-apron-assistant.isReady', { isReady: false });
  };

  const handleMagicApronEvents = ({ eventName, output }) => {
    if (eventName === 'question-ask') {
      const {
        messageOrigin,
        pageType,
        entryLocationForAnalytics,
        userQuestion,
        sessionID,
        actionID
      } = output;
      updateSession({ shouldAutoOpen: true });
      const question = [
        MAGIC_APRON_ASSISTANT_ORIGIN,
        entryLocationForAnalytics,
        pageType,
        messageOrigin,
        userQuestion
      ].filter(Boolean).join(':');
      window.LIFE_CYCLE_EVENT_BUS.trigger('magic-apron-assistant.submit-click', {
        sessionID,
        question,
        actionID,
      });
    }
  };

  useEffect(() => {
    // Derive shouldAutoOpen once: on expiry setSession doesn't update sessionRef.current
    // synchronously, so reading the ref again below would see the pre-reset value.
    const expired = isSessionExpired(sessionRef.current.timestamp);
    const shouldAutoOpen = expired
      ? sessionRef.current.shouldAutoOpen !== false
      : sessionRef.current?.shouldAutoOpen;

    if (expired) {
      setSession({
        ...defaultSession,
        shouldAutoOpen,
      });
    }

    if (shouldAutoOpen) {
      let autoOpenState = 'OPEN';
      if (isMobile) autoOpenState = enableMiniDrawer ? 'MINIMIZED' : 'CLOSED';
      updateDrawerState(autoOpenState);
    }

    // Listen for communication from component and cross-tab state synchronization
    broadcastChannel.current.onmessage = ({ data: { type, data } }) => {
      if (type === 'MAA_OPEN_DRAWER') {
        handleOpen(false);
      }
      if (type === 'MAA_PRODUCT_POD_CLICK') {
        window.LIFE_CYCLE_EVENT_BUS.trigger('magic-apron-assistant.product-pod-click', data);
      }
      if (type === 'MAA_PRODUCT_POD_ATC_CLICK') {
        window.LIFE_CYCLE_EVENT_BUS.trigger('magic-apron-assistant.product-pod-atc-click', data);
      }
      // Handle cross-tab drawer state synchronization for mobile app
      if (type === 'INTERNAL_ONLY_MAA_DRAWER_STATE_CHANGED' && isConsumerApp) {
        updateDrawerState(data.drawerState, { skipBroadcast: true });
      }
    };

    window.LIFE_CYCLE_EVENT_BUS.on('magic-apron-assistant.*', handleMagicApronEvents);

    return () => {
      broadcastChannel.current.close();
      window.LIFE_CYCLE_EVENT_BUS.off('magic-apron-assistant.*', handleMagicApronEvents);
    };
  }, []);

  useEffect(() => {
    window.LIFE_CYCLE_EVENT_BUS.trigger('magic-apron-assistant.drawer-minimize', drawerState);
  }, [drawerState]);

  // On tablets, when changing orientation layout can change between open and minimized
  useEffect(() => {
    if (isMobile && drawerState === 'OPEN') {
      updateDrawerState(enableMiniDrawer ? 'MINIMIZED' : 'CLOSED');
    }
    if (!isMobile && drawerState === 'MINIMIZED') {
      updateDrawerState('OPEN');
    }
  }, [isMobile]);

  if (!isCompatiblePage) return null;

  if (!(
    (fsDTEnabled && !isMobile)
    || (fsMWEnabled && isMobile && !isConsumerApp)
    || (fsAPPEnabled && isMobile && isConsumerApp)
  ) || !isValidCustomerType
  ) return null;

  // Drawer state and its handlers live here so they persist across a switch of the
  // rendered App tree; the selected tree only renders the (package-specific)
  // assistant content from these props.
  const drawerProps = {
    drawerState,
    offsetMargin,
    enableMiniDrawer,
    session,
    isMobile,
    handleClose,
    handleOpen,
    updateDrawerState,
    updateSession,
  };

  return (
    <>
      {(drawerState === 'CLOSED') && (
        <ImpressionProvider
          data={{
            id: MAGIC_APRON_CONTAINER_ID,
            component: 'MagicApronAssistant',
            name: 'MagicApronLoader',
            type: 'content'
          }}
        >
          <AssistantActionButton
            offsetMargin={offsetMargin}
            onClick={() => handleOpen(true)}
          />
        </ImpressionProvider>
      )}
      <Suspense fallback={null}>
        {/* eslint-disable-next-line react/jsx-props-no-spreading */}
        {SelectedApp ? <SelectedApp {...drawerProps} /> : null}
      </Suspense>
    </>
  );
};

AppSwitch.propTypes = {
  activateStyles: PropTypes.func,
};
